import LocomotiveScroll from "locomotive-scroll";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

import { varGutter, prefersReducedMotion, isTouchDevice } from "../utils/variables";

export let scroll: LocomotiveScroll;

let scrollTriggerRefreshWired = false;
let scheduledRefreshTimeout: number | null = null;
let scheduledRefreshRaf: number | null = null;
let lastRefreshAt = 0;

function scheduleScrollTriggerRefresh(delayMs: number = 0) {
    if (scheduledRefreshTimeout !== null) window.clearTimeout(scheduledRefreshTimeout);
    scheduledRefreshTimeout = window.setTimeout(() => {
        if (scheduledRefreshRaf !== null) cancelAnimationFrame(scheduledRefreshRaf);

        scheduledRefreshRaf = requestAnimationFrame(() => {
            const minRefreshInterval = isTouchDevice ? 300 : 100;
            const elapsed = Date.now() - lastRefreshAt;

            const runRefresh = () => {
                lastRefreshAt = Date.now();
                ScrollTrigger.refresh();
            };

            if (elapsed < minRefreshInterval) {
                window.setTimeout(runRefresh, minRefreshInterval - elapsed);
                return;
            }

            runRefresh();
        });
    }, delayMs);
}

export function requestScrollTriggerRefresh(delayMs: number = 0) {
    scheduleScrollTriggerRefresh(delayMs);
}

function initScrollTriggerRefreshHandling() {
    if (scrollTriggerRefreshWired) return;
    scrollTriggerRefreshWired = true;

    ScrollTrigger.addEventListener("refreshInit", () => {
        (scroll as unknown as { resize?: () => void })?.resize?.();
    });

    window.addEventListener(
        "load",
        () => {
            scheduleScrollTriggerRefresh(0);
        },
        { once: true }
    );

    if ("fonts" in document) {
        document.fonts?.ready?.then?.(() => scheduleScrollTriggerRefresh(0));
    }

    if (!isTouchDevice) {
        const onResourceLoad = (e: Event) => {
            const target = e.target;
            if (target instanceof HTMLImageElement || target instanceof HTMLVideoElement || target instanceof HTMLIFrameElement) {
                scheduleScrollTriggerRefresh(100);
            }
        };

        document.addEventListener("load", onResourceLoad, true);
        document.addEventListener("loadedmetadata", onResourceLoad, true);
        document.addEventListener("loadeddata", onResourceLoad, true);
    }
}

export function initScroller() {
    scroll = new LocomotiveScroll({
        lenisOptions: {
            duration: prefersReducedMotion ? 0 : 1,
            easing: prefersReducedMotion ? t => t : t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: !prefersReducedMotion,
            wheelMultiplier: 1,
            infinite: false,
            syncTouch: true,
            prevent: node => {
                if (node.nodeName === "VERCEL-LIVE-FEEDBACK") return true;
                return false;
            },
        },
        initCustomTicker: render => gsap.ticker.add(render),
        destroyCustomTicker: render => gsap.ticker.remove(render),
    });

    scroll.lenisInstance?.on("scroll", ScrollTrigger.update);
    gsap.ticker.lagSmoothing(0);

    initScrollTriggerRefreshHandling();

    if (prefersReducedMotion) {
        gsap.globalTimeline.timeScale(9999);
    }

    initJumpLinksScrollTo();
    checkInitialHash();
}

function initJumpLinksScrollTo() {
    const jumpLinks = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');

    jumpLinks.forEach(anchor => {
        anchor.addEventListener("click", function (e) {
            e.preventDefault();

            if (location.pathname.replace(/^\//, "") === anchor.pathname.replace(/^\//, "") && location.hostname === anchor.hostname) {
                const targetId = anchor.hash;
                const target = document.querySelector(targetId) as HTMLElement;

                if (target) {
                    scroll.scrollTo(target, {
                        offset: -varGutter,
                    });
                    history.pushState(null, "", targetId);
                }
            }
        });
    });
}

function checkInitialHash() {
    const hash = window.location.hash;
    if (!hash) return;

    const target = document.querySelector<HTMLElement>(hash);
    if (target) {
        scroll.scrollTo(target, {
            offset: -varGutter,
        });

        scheduleScrollTriggerRefresh(100);
    }
}
